import { useEffect, useRef, useState } from "react"
import { MoreHorizontal, Plus, X } from "lucide-react"
import {
  BrandLockup,
  LogoutForm,
  Notice,
  WorkspaceSidebar,
  readSidebarCollapsedState,
  shouldHandleClientNavigation,
  workspaceSectionHref,
  workspaceSectionPathsFor,
  writeSidebarCollapsedState
} from "../components/Chrome"
import { HiddenField, MethodField } from "../lib/rails"
import { useDismissiblePopover } from "../lib/useDismissiblePopover"

export function TeamPage({ alert, csrfToken, githubLogin, invitation, paths, team }) {
  if (invitation) {
    return (
      <section className="team-page-shell">
        <div className="team-page-topbar">
          <BrandLockup href={paths.root} kicker="Team invitation" />
          {githubLogin ? (
            <LogoutForm action={paths.githubSession} csrfToken={csrfToken} className="welcome-secondary-link" />
          ) : null}
        </div>

        <Notice alert={alert} />

        <div className="team-invitation-card focus-card slide-up">
          <div className="section-label">Invitation</div>
          <h1 className="team-page-title">{invitation.workspaceName}</h1>
          <p className="team-page-copy">{invitationCopy(invitation, githubLogin)}</p>

          {githubLogin ? (
            <form method="post" action={paths.acceptInvitation} className="single-action-form">
              <MethodField method="patch" />
              <HiddenField name="authenticity_token" value={csrfToken} />
              <button type="submit" className="primary-action-button" disabled={!invitation.pending}>
                Accept invitation
              </button>
            </form>
          ) : (
            <a href={paths.newGithubAuthorization} className="primary-action-button">
              Continue with GitHub
            </a>
          )}
        </div>
      </section>
    )
  }

  return (
    <TeamWorkspaceShell
      alert={alert}
      csrfToken={csrfToken}
      githubLogin={githubLogin}
      paths={paths}
      team={team}
    />
  )
}

export function TeamPanel({ csrfToken, embedded = false, team }) {
  const members = team?.members || []
  const invitations = team?.invitations || []
  const canInvite = team?.canInvite
  const [inviteOpen, setInviteOpen] = useState(false)

  return (
    <div className={`team-panel ${embedded ? "is-embedded" : ""}`.trim()}>
      <section className="focus-card team-card slide-up slide-up-delayed">
        <div className="team-card-header">
          <div className="section-label">Members</div>
          {canInvite ? (
            <button
              type="button"
              className="team-invite-button"
              onClick={() => setInviteOpen(true)}
            >
              <Plus size={14} aria-hidden="true" />
              <span>Add</span>
            </button>
          ) : null}
        </div>
        <div className="team-list">
          {members.length ? members.map((member) => (
            <MemberRow
              key={member.id || member.githubLogin}
              member={member}
              csrfToken={csrfToken}
              currentUserGithubLogin={team?.currentUserGithubLogin}
            />
          )) : (
            <EmptyTeamState text="No members have joined this workspace yet." />
          )}
        </div>

        {invitations.length ? (
          <div className="team-pending-section">
            <div className="section-label">Pending invites</div>
            <div className="team-list">
              {invitations.map((invitation) => (
                <PendingInvitationRow
                  key={`${invitation.invitedIdentifier}-${invitation.expiresAt || invitation.status}`}
                  invitation={invitation}
                />
              ))}
            </div>
          </div>
        ) : null}
      </section>

      {inviteOpen ? (
        <InviteModal
          csrfToken={csrfToken}
          action={team?.paths?.createCollaborator}
          onClose={() => setInviteOpen(false)}
        />
      ) : null}
    </div>
  )
}

function TeamWorkspaceShell({ alert, csrfToken, githubLogin, paths, team }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readSidebarCollapsedState)
  const repository = primaryRepository(team)
  const workspaceSectionPaths = workspaceSectionPathsFor(repository, paths.root)

  useEffect(() => {
    writeSidebarCollapsedState(sidebarCollapsed)
  }, [sidebarCollapsed])

  function handleWorkspaceNavigate(section, event) {
    if (event && !shouldHandleClientNavigation(event)) return

    if (section === "team") {
      event?.preventDefault()
      return
    }

    const path = workspaceSectionPaths[section] || workspaceSectionHref(repository, section, paths?.root)
    event?.preventDefault()
    if (path) window.location.href = path
  }

  return (
    <div className={`workspace-shell team-workspace-shell ${sidebarCollapsed ? "is-sidebar-collapsed" : ""}`.trim()}>
      <WorkspaceSidebar
        activeSection="team"
        collapsed={sidebarCollapsed}
        csrfToken={csrfToken}
        githubLogin={githubLogin || "GitHub"}
        onNavigate={handleWorkspaceNavigate}
        onToggleCollapsed={() => setSidebarCollapsed((current) => !current)}
        paths={{ githubSession: paths.githubSession }}
        repositories={team?.repositories || []}
        repository={repository}
        rootPath={paths.root}
        sectionPaths={workspaceSectionPaths}
      />

      <main className="workspace-main">
        <section className="content-stack page-scroll workspace-flow">
          <div className="team-mobile-topbar">
            <BrandLockup href={paths.root} kicker="Team" />
            <LogoutForm action={paths.githubSession} csrfToken={csrfToken} className="welcome-secondary-link" />
          </div>

          <Notice alert={alert} />

          <div className="system-hero slide-up">
            <div className="system-hero-main">
              <h1 className="page-title">Team</h1>
            </div>
          </div>

          <TeamPanel csrfToken={csrfToken} embedded team={team} />
        </section>
      </main>
    </div>
  )
}

function MemberRow({ csrfToken, currentUserGithubLogin, member }) {
  const githubUrl = member.githubLogin ? `https://github.com/${encodeURIComponent(member.githubLogin)}` : null
  const addedLabel = formatAddedDate(member.addedAt)
  const isSelf = currentUserGithubLogin && member.githubLogin === currentUserGithubLogin

  return (
    <div className="team-member-row">
      <span className="team-avatar" aria-hidden="true">
        {member.avatarUrl ? <img src={member.avatarUrl} alt="" /> : member.githubLogin?.slice(0, 1).toUpperCase()}
      </span>
      <span className="team-member-details">
        <strong>{member.name || member.githubLogin}</strong>
        <span className="team-member-meta">
          {githubUrl ? (
            <a
              className="team-member-handle"
              href={githubUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              @{member.githubLogin}
            </a>
          ) : (
            <span className="team-member-handle">@{member.githubLogin}</span>
          )}
          {addedLabel ? <span className="team-member-added"> · Added {addedLabel}</span> : null}
        </span>
      </span>
      {member.removePath ? (
        <MemberRowMenu
          action={member.removePath}
          csrfToken={csrfToken}
          isSelf={isSelf}
          memberName={member.name || member.githubLogin}
        />
      ) : null}
    </div>
  )
}

function PendingInvitationRow({ invitation }) {
  const expired = invitation.status === "expired"
  const statusLabel = expired ? "Expired" : "Pending"
  const expirationLabel = formatExpirationDate(invitation.expiresAt)

  return (
    <div className="team-invitation-row">
      <span className="team-invitation-dot" aria-hidden="true"></span>
      <span className="team-member-details">
        <strong>{invitation.invitedIdentifier}</strong>
        <span className="team-member-meta">
          <span className="team-member-handle">{statusLabel}</span>
          {!expired && expirationLabel ? <span className="team-member-added"> · Expires {expirationLabel}</span> : null}
        </span>
      </span>
    </div>
  )
}

function MemberRowMenu({ action, csrfToken, isSelf, memberName }) {
  const [open, setOpen] = useState(false)
  const menuRef = useDismissiblePopover(open, () => setOpen(false))

  function handleSubmit(event) {
    const verb = isSelf ? "leave this workspace" : `remove ${memberName} from this workspace`
    if (!window.confirm(`Are you sure you want to ${verb}?`)) {
      event.preventDefault()
    }
  }

  return (
    <div className="team-member-menu" ref={menuRef}>
      <button
        type="button"
        className="team-member-menu-trigger"
        aria-label="Member actions"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((current) => !current)}
      >
        <MoreHorizontal size={16} aria-hidden="true" />
      </button>

      {open ? (
        <div className="team-member-menu-popover" role="menu">
          <form method="post" action={action} onSubmit={handleSubmit}>
            <MethodField method="delete" />
            <HiddenField name="authenticity_token" value={csrfToken} />
            <button type="submit" className="team-member-menu-item is-destructive" role="menuitem">
              {isSelf ? "Leave workspace" : "Remove from workspace"}
            </button>
          </form>
        </div>
      ) : null}
    </div>
  )
}

function InviteModal({ action, csrfToken, onClose }) {
  const inputRef = useRef(null)

  useEffect(() => {
    inputRef.current?.focus()

    function handleKeyDown(event) {
      if (event.key === "Escape") onClose()
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [onClose])

  function handleBackdropClick(event) {
    if (event.target === event.currentTarget) onClose()
  }

  return (
    <div className="stack-modal-backdrop" onClick={handleBackdropClick} role="presentation">
      <div className="focus-card stack-selection-modal team-invite-modal" role="dialog" aria-modal="true" aria-labelledby="team-invite-title">
        <header className="team-invite-modal-header">
          <h2 id="team-invite-title" className="team-card-title">Invite a teammate</h2>
          <button type="button" className="team-invite-modal-close" aria-label="Close" onClick={onClose}>
            <X size={16} aria-hidden="true" />
          </button>
        </header>

        <p className="team-page-copy stack-modal-copy">
          Send a GitHub collaborator invitation for the repositories in this workspace.
        </p>

        <form method="post" action={action} className="team-invite-form">
          <HiddenField name="authenticity_token" value={csrfToken} />
          <label className="team-invite-field">
            <span className="section-label">GitHub username or email</span>
            <input
              ref={inputRef}
              type="text"
              name="workspace_collaborator[identifier]"
              required
              autoComplete="off"
              className="team-invite-input"
              placeholder="octocat"
            />
          </label>
          <div className="team-invite-actions">
            <button type="button" className="team-invite-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="team-invite-submit">
              Add collaborator
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function EmptyTeamState({ text }) {
  return <p className="team-empty-text">{text}</p>
}

function formatAddedDate(isoString) {
  if (!isoString) return null

  const date = new Date(isoString)
  if (Number.isNaN(date.valueOf())) return null

  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
}

function formatExpirationDate(isoString) {
  return formatAddedDate(isoString)
}

function primaryRepository(team) {
  const repository = (team?.repositories || []).find((option) => option.fullName) || null
  if (!repository) return null

  return {
    ...repository,
    workspaceSlug: repository.workspaceSlug || team?.workspace?.slug
  }
}

function invitationCopy(invitation, githubLogin) {
  if (invitation.accepted) return "This invitation has already been accepted."
  if (invitation.expired) return "This invitation has expired."
  if (githubLogin) return `Accept as ${githubLogin} after GitHub confirms repository access.`

  return "Connect with GitHub to verify repository access before joining."
}
